/**
 * AdminOverview — 老板数据仪表盘 v3
 *
 * 数据拉取规则（严格对齐业务定义）：
 *   在产任务：production_tasks.stage IN ('scheduling','raw_material','vulcanizing','qc_passed','packing')
 *   待发货：  production_tasks.stage = 'packing'（包装待发 = 待发货）
 *   已完成订单：orders.order_status = 'completed'
 *   全部使用 count:'exact' + head:true，不拉全量数据到前端
 *   字段名严格对齐数据库：inquiries.company / inquiries.material
 *   orders 表已删除 material_code 字段，不再查询
 */
import { useEffect, useState, useRef } from 'react';
import {
  RefreshCw, Users, Package2, TrendingUp,
  Truck, CheckCircle2, AlertCircle, Activity,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

// ── 颜色常量 ─────────────────────────────────────────
const ORANGE    = 'oklch(0.66 0.19 42)';
const CARD_BG   = '#FFFFFF';
const HEADER_BG = '#F0F2F5';

// ── 在产阶段枚举（英文，与数据库 stage 字段一致） ───────
// scheduling   → 排产中
// raw_material → 原材料准备
// vulcanizing  → 硫化/切割中
// qc_passed    → 质检完成
// packing      → 包装待发（同时 = 物流待发货）
const ALL_PRODUCING_STAGES = [
  'scheduling',
  'raw_material',
  'vulcanizing',
  'qc_passed',
  'packing',
] as const;

const STAGE_LABEL: Record<string, string> = {
  scheduling:   '排产中',
  raw_material: '原材料准备',
  vulcanizing:  '硫化/切割中',
  qc_passed:    '质检完成',
  packing:      '包装待发',
};

// ── 统计接口 ──────────────────────────────────────────
interface Stats {
  totalInquiries:   number;
  newInquiries:     number;
  completedOrders:  number;
  totalOrders:      number;
  producingTasks:   number;   // 全部在产（5 个阶段合计）
  packingTasks:     number;   // 包装待发 = 待发货
  monthlyAmount:    number;
  totalAmount:      number;
  stageBreakdown:   Record<string, number>; // 每个阶段的任务数
}

interface RecentInquiry {
  id: string;
  name: string;
  company: string | null;
  material: string | null;
  status: string;
  created_at: string;
}

interface RecentOrder {
  id: string;
  order_no: string;
  total_amount: number | null;
  order_status: string;
  created_at: string;
}

// ── 工具函数 ──────────────────────────────────────────
function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return '刚刚';
  if (m < 60) return `${m} 分钟前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小时前`;
  return `${Math.floor(h / 24)} 天前`;
}

function fmtMoney(n: number) {
  if (n >= 10000) return `¥${(n / 10000).toFixed(1)}万`;
  return `¥${n.toLocaleString('zh-CN')}`;
}

// ── KPI 卡片 ──────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, accent = false, warn = false }: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  accent?: boolean;
  warn?: boolean;
}) {
  const valueColor = accent ? ORANGE : warn ? '#FBBF24' : '#1A2337';
  return (
    <div className="rounded-sm border border-border p-5 flex flex-col gap-3" style={{ background: CARD_BG }}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono tracking-widest uppercase" style={{ color: '#5A6780' }}>{label}</span>
        <div className="w-8 h-8 rounded-sm flex items-center justify-center"
          style={{ background: accent ? 'rgba(232,119,34,0.12)' : warn ? 'rgba(251,191,36,0.10)' : 'rgba(0,0,0,0.04)' }}>
          <Icon className="w-4 h-4" style={{ color: accent ? ORANGE : warn ? '#FBBF24' : '#5A6780' }} />
        </div>
      </div>
      <div className="text-3xl font-black" style={{ color: valueColor }}>{value}</div>
      {sub && <div className="text-xs" style={{ color: '#5A6780' }}>{sub}</div>}
    </div>
  );
}

// ── 横向条形图（单条） ────────────────────────────────
function MiniBar({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span style={{ color: '#C5D0E0' }}>{label}</span>
        <span className="font-mono font-bold tabular-nums" style={{ color }}>{value} <span style={{ color: '#5A6780', fontWeight: 400 }}>({pct}%)</span></span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#F0F2F5' }}>
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// ── 主组件 ────────────────────────────────────────────
export function OverviewView() {
  const [stats, setStats]               = useState<Stats | null>(null);
  const [recentInq, setRecentInq]       = useState<RecentInquiry[]>([]);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [lastUpdated, setLastUpdated]   = useState<Date | null>(null);
  const [err, setErr]                   = useState('');
  const [refreshKey, setRefreshKey]     = useState(0);
  const fetchRef = useRef(0);

  useEffect(() => {
    const ticket = ++fetchRef.current;
    const isRefresh = stats !== null;
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setErr('');

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    Promise.all([
      // 1. 总线索数
      supabase.from('inquiries').select('*', { count: 'exact', head: true }),

      // 2. 新线索数
      supabase.from('inquiries').select('*', { count: 'exact', head: true }).eq('status', 'new'),

      // 3. 已完成订单数
      supabase.from('orders').select('*', { count: 'exact', head: true }).eq('order_status', 'completed'),

      // 4. 订单总数
      supabase.from('orders').select('*', { count: 'exact', head: true }),

      // 5. ★ 在产任务数：精确匹配 5 个在产阶段（英文枚举值）
      supabase.from('production_tasks')
        .select('*', { count: 'exact', head: true })
        .in('stage', ALL_PRODUCING_STAGES as unknown as string[]),

      // 6. ★ 待发货数：packing = 包装待发 = 物流待发货（业务规则 2）
      supabase.from('production_tasks')
        .select('*', { count: 'exact', head: true })
        .eq('stage', 'packing'),

      // 7. 各阶段明细（用于生产状态分布图，每个阶段单独 count）
      ...ALL_PRODUCING_STAGES.map(stage =>
        supabase.from('production_tasks')
          .select('*', { count: 'exact', head: true })
          .eq('stage', stage)
      ),

      // 8. 本月订单金额
      supabase.from('orders').select('total_amount').gte('created_at', monthStart),

      // 9. 全量订单金额
      supabase.from('orders').select('total_amount'),

      // 10. 最新 5 条线索（字段名对齐数据库：company / material）
      supabase.from('inquiries')
        .select('id, name, company, material, status, created_at')
        .order('created_at', { ascending: false })
        .limit(5),

      // 11. 最新 5 条订单（不查已删除的 material_code）
      supabase.from('orders')
        .select('id, order_no, total_amount, order_status, created_at')
        .order('created_at', { ascending: false })
        .limit(5),

    ]).then(results => {
      if (ticket !== fetchRef.current) return;

      const [
        inqAll, inqNew, ordDone, ordAll,
        taskAll, taskPacking,
        ...rest
      ] = results;

      // 解构各阶段 count（5 个）+ 金额查询（2个）+ 最新数据（2个）
      const stageCounts = rest.slice(0, ALL_PRODUCING_STAGES.length) as { count: number | null }[];
      const [ordMonth, ordTotal, latestInq, latestOrd] = rest.slice(ALL_PRODUCING_STAGES.length);

      type AmtRow = { total_amount: number | null };
      const monthlyAmount = ((ordMonth as { data: AmtRow[] | null }).data ?? [])
        .reduce((s, o) => s + (o.total_amount ?? 0), 0);
      const totalAmount = ((ordTotal as { data: AmtRow[] | null }).data ?? [])
        .reduce((s, o) => s + (o.total_amount ?? 0), 0);

      const stageBreakdown: Record<string, number> = {};
      ALL_PRODUCING_STAGES.forEach((stage, idx) => {
        stageBreakdown[stage] = stageCounts[idx]?.count ?? 0;
      });

      setStats({
        totalInquiries:  inqAll.count  ?? 0,
        newInquiries:    inqNew.count  ?? 0,
        completedOrders: ordDone.count ?? 0,
        totalOrders:     ordAll.count  ?? 0,
        producingTasks:  taskAll.count ?? 0,
        packingTasks:    taskPacking.count ?? 0,
        monthlyAmount,
        totalAmount,
        stageBreakdown,
      });

      setRecentInq(((latestInq as { data: RecentInquiry[] | null }).data ?? []) as RecentInquiry[]);
      setRecentOrders(((latestOrd as { data: RecentOrder[] | null }).data ?? []) as RecentOrder[]);
      setLastUpdated(new Date());
      setLoading(false);
      setRefreshing(false);

      const anyErr = [inqAll, inqNew, ordDone, ordAll, taskAll, taskPacking].find(r => r.error);
      if (anyErr?.error) setErr(`部分数据加载失败：${anyErr.error.message}`);
    });
  // refreshKey 变化时重新拉取；组件挂载也自动执行
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="p-16 flex flex-col items-center gap-4" style={{ color: '#5A6780' }}>
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: ORANGE }} />
        <span className="text-sm">正在实时查询数据库…</span>
      </div>
    );
  }

  const s = stats!;
  const totalProducing = s.producingTasks;

  return (
    <div className="space-y-6">

      {/* 顶部工具栏 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A2337' }}>
            <Activity className="w-4 h-4" style={{ color: ORANGE }} />
            实时数据概览
          </h2>
          {lastUpdated && (
            <p className="text-xs mt-0.5" style={{ color: '#5A6780' }}>
              更新于 {lastUpdated.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              {' · '}切换 Tab 自动刷新
            </p>
          )}
        </div>
        <button
          onClick={() => setRefreshKey(k => k + 1)}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-border disabled:opacity-50 transition-colors hover:border-orange-400/30"
          style={{ color: '#5A6780' }}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? '刷新中…' : '立即刷新'}
        </button>
      </div>

      {/* 错误提示 */}
      {err && (
        <div className="flex items-center gap-2 rounded-sm px-4 py-3 border border-red-400/20 text-xs"
          style={{ background: 'rgba(248,113,113,0.08)', color: '#F87171' }}>
          <AlertCircle className="w-4 h-4 flex-shrink-0" />{err}
        </div>
      )}

      {/* ── KPI 卡片（6张）── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard icon={Users}        label="总线索数"   value={s.totalInquiries}  sub={`新线索 ${s.newInquiries} 条待跟进`}                accent />
        <KpiCard icon={TrendingUp}   label="本月订单额" value={fmtMoney(s.monthlyAmount)} sub={`累计 ${fmtMoney(s.totalAmount)}`}          accent />
        <KpiCard icon={CheckCircle2} label="已完成订单" value={s.completedOrders} sub={`共 ${s.totalOrders} 个订单`} />
        <KpiCard icon={Package2}     label="在产任务数" value={s.producingTasks}
          sub="排产·备料·硫化·质检·包装 合计" warn={s.producingTasks > 0} />
        <KpiCard icon={Truck}        label="包装待发（待发货）" value={s.packingTasks}
          sub="packing 阶段 = 物流待发货" warn={s.packingTasks > 0} />
        <KpiCard icon={CheckCircle2} label="新线索" value={s.newInquiries}
          sub={`转化率 ${s.totalInquiries > 0 ? Math.round((s.completedOrders / s.totalInquiries) * 100) : 0}%`} />
      </div>

      {/* ── 图表区 ── */}
      <div className="grid md:grid-cols-2 gap-4">

        {/* 生产阶段分布（精准对齐 5 个英文枚举） */}
        <div className="rounded-sm border border-border p-5" style={{ background: CARD_BG }}>
          <h3 className="text-[10px] font-mono font-bold tracking-widest uppercase mb-1" style={{ color: '#5A6780' }}>
            生产阶段分布
          </h3>
          <p className="text-[10px] mb-4" style={{ color: '#4A5F7A' }}>
            在产合计：{totalProducing} 个任务
          </p>
          <div className="space-y-3">
            {ALL_PRODUCING_STAGES.map((stage, i) => {
              const colors = [ORANGE, '#60A5FA', '#FB923C', '#A78BFA', '#34D399'];
              return (
                <MiniBar
                  key={stage}
                  label={`${STAGE_LABEL[stage]}${stage === 'packing' ? '（= 待发货）' : ''}`}
                  value={s.stageBreakdown[stage] ?? 0}
                  total={Math.max(totalProducing, 1)}
                  color={colors[i]}
                />
              );
            })}
          </div>
          {/* 枚举对照说明 */}
          <div className="mt-4 pt-3 border-t border-border grid grid-cols-2 gap-x-4 gap-y-1">
            {ALL_PRODUCING_STAGES.map(stage => (
              <div key={stage} className="flex items-center gap-1 text-[10px]" style={{ color: '#4A5F7A' }}>
                <span className="font-mono">{stage}</span>
                <span>→ {STAGE_LABEL[stage]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 订单 & 线索概况 */}
        <div className="rounded-sm border border-border p-5" style={{ background: CARD_BG }}>
          <h3 className="text-[10px] font-mono font-bold tracking-widest uppercase mb-4" style={{ color: '#5A6780' }}>
            订单 & 线索概况
          </h3>
          <div className="space-y-3">
            <MiniBar label="总线索" value={s.totalInquiries} total={Math.max(s.totalInquiries, 1)} color={ORANGE} />
            <MiniBar label="新线索（未跟进）" value={s.newInquiries} total={Math.max(s.totalInquiries, 1)} color="#FBBF24" />
            <MiniBar label="总订单" value={s.totalOrders} total={Math.max(s.totalOrders, 1)} color="#60A5FA" />
            <MiniBar label="已完成订单" value={s.completedOrders} total={Math.max(s.totalOrders, 1)} color="#34D399" />
            <MiniBar label="待发货（packing）" value={s.packingTasks} total={Math.max(s.producingTasks, 1)} color="#A78BFA" />
          </div>
        </div>
      </div>

      {/* ── 最新动态 ── */}
      <div className="grid md:grid-cols-2 gap-4">

        {/* 最新线索 */}
        <div className="rounded-sm border border-border overflow-hidden" style={{ background: CARD_BG }}>
          <div className="px-5 py-3 border-b border-border flex items-center justify-between" style={{ background: HEADER_BG }}>
            <h3 className="text-[10px] font-mono font-bold tracking-widest uppercase" style={{ color: ORANGE }}>最新线索</h3>
            <span className="text-[10px]" style={{ color: '#5A6780' }}>最近 5 条</span>
          </div>
          {recentInq.length === 0 ? (
            <div className="p-8 flex flex-col items-center gap-2" style={{ color: '#5A6780' }}>
              <AlertCircle className="w-6 h-6" /><span className="text-xs">暂无线索数据</span>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {recentInq.map(inq => (
                <div key={inq.id}
                  className="flex items-center gap-3 px-5 py-3 transition-colors"
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#F7F8FA'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#F0F2F5' }}>
                    <Users className="w-3.5 h-3.5" style={{ color: '#5A6780' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate" style={{ color: '#1A2337' }}>
                      {inq.name}{inq.company ? ` · ${inq.company}` : ''}
                    </div>
                    <div className="text-xs truncate" style={{ color: '#5A6780' }}>{inq.material ?? '未指定材料'}</div>
                  </div>
                  <div className="text-right flex-shrink-0 space-y-0.5">
                    <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${
                      inq.status === 'new'    ? 'text-yellow-400 bg-yellow-400/10' :
                      inq.status === 'won'    ? 'text-green-400  bg-green-400/10'  :
                      inq.status === 'lost'   ? 'text-gray-400   bg-gray-400/10'   :
                      inq.status === 'quoted' ? 'text-purple-400 bg-purple-400/10' :
                      'text-blue-400 bg-blue-400/10'
                    }`}>{inq.status.toUpperCase()}</div>
                    <div className="text-[10px]" style={{ color: '#5A6780' }}>{timeAgo(inq.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 最新订单 */}
        <div className="rounded-sm border border-border overflow-hidden" style={{ background: CARD_BG }}>
          <div className="px-5 py-3 border-b border-border flex items-center justify-between" style={{ background: HEADER_BG }}>
            <h3 className="text-[10px] font-mono font-bold tracking-widest uppercase" style={{ color: ORANGE }}>最新订单</h3>
            <span className="text-[10px]" style={{ color: '#5A6780' }}>最近 5 条</span>
          </div>
          {recentOrders.length === 0 ? (
            <div className="p-8 flex flex-col items-center gap-2" style={{ color: '#5A6780' }}>
              <AlertCircle className="w-6 h-6" /><span className="text-xs">暂无订单数据</span>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {recentOrders.map(order => (
                <div key={order.id}
                  className="flex items-center gap-3 px-5 py-3 transition-colors"
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#F7F8FA'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#F0F2F5' }}>
                    <Truck className="w-3.5 h-3.5" style={{ color: '#5A6780' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono font-bold" style={{ color: ORANGE }}>{order.order_no}</div>
                    <div className="text-xs" style={{ color: '#5A6780' }}>
                      {order.order_status === 'completed' ? '✅ 已完成' :
                       order.order_status === 'pending'   ? '🕐 待生产' :
                       order.order_status === 'producing' ? '⚙️ 生产中' :
                       order.order_status === 'shipped'   ? '🚚 已发货' :
                       order.order_status}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs font-bold font-mono" style={{ color: '#1A2337' }}>
                      {order.total_amount != null
                        ? `¥${order.total_amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`
                        : '—'}
                    </div>
                    <div className="text-[10px] mt-0.5" style={{ color: '#5A6780' }}>{timeAgo(order.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 业务流程指引 */}
      <div className="rounded-sm border border-blue-400/20 p-5 space-y-2" style={{ background: 'rgba(96,165,250,0.05)' }}>
        <p className="text-xs font-bold" style={{ color: '#60A5FA' }}>📊 系统业务闭环 · 数据追踪链路</p>
        <p className="text-xs leading-loose" style={{ color: '#5A6780' }}>
          <span style={{ color: '#1A2337' }}>客户咨询</span> →{' '}
          <span style={{ color: '#1A2337' }}>CRM线索</span> →{' '}
          <span style={{ color: '#1A2337' }}>核价报价</span> →{' '}
          <span style={{ color: '#1A2337' }}>转为订单</span> →{' '}
          <span style={{ color: '#1A2337' }}>排产({`scheduling`})</span> →{' '}
          <span style={{ color: '#1A2337' }}>备料({`raw_material`})</span> →{' '}
          <span style={{ color: '#1A2337' }}>硫化({`vulcanizing`})</span> →{' '}
          <span style={{ color: '#1A2337' }}>质检({`qc_passed`})</span> →{' '}
          <span style={{ color: ORANGE }}>包装待发({`packing`}) = 待发货</span> →{' '}
          <span style={{ color: '#34D399' }}>完成</span>
        </p>
      </div>
    </div>
  );
}
